import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from "../../context/AuthContext";
import { useMasterAuth } from "../../context/MasterAuthContext";

const Layout = ({ children }) => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const { user, logout } = useAuth();
  const { masterUser, masterLogout } = useMasterAuth();
  const navigate = useNavigate();
  const location = useLocation();

  // Determine which user is active (master overrides school user)
  const activeUser = masterUser || user;
  const activeRole = masterUser?.role || user?.role;

  // Combined logout
  const handleLogout = () => {
    if (masterUser) {
      masterLogout();
    } else {
      logout();
    }
  };

  // Detect mobile screen size
  useEffect(() => {
    const checkMobile = () => {
      const mobile = window.innerWidth < 1024;
      setIsMobile(mobile);
      if (!mobile && sidebarOpen) {
        setSidebarOpen(false);
      }
    };
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, [sidebarOpen]);

  const handleLinkClick = () => {
    if (isMobile) {
      setSidebarOpen(false);
    }
  };

  const toggleSidebar = (e) => {
    if (e) e.stopPropagation();
    setSidebarOpen(!sidebarOpen);
  };

  // Close sidebar on outside click (mobile)
  useEffect(() => {
    const handleOutsideClick = (e) => {
      if (sidebarOpen && isMobile) {
        const sidebar = document.querySelector('.sidebar-container');
        const menuButton = document.querySelector('.menu-toggle-button');
        if (sidebar && !sidebar.contains(e.target) && menuButton && !menuButton.contains(e.target)) {
          setSidebarOpen(false);
        }
      }
    };
    document.addEventListener('click', handleOutsideClick);
    document.addEventListener('touchstart', handleOutsideClick);
    return () => {
      document.removeEventListener('click', handleOutsideClick);
      document.removeEventListener('touchstart', handleOutsideClick);
    };
  }, [sidebarOpen, isMobile]);

  // Escape key closes sidebar on mobile
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape' && sidebarOpen && isMobile) {
        setSidebarOpen(false);
      }
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [sidebarOpen, isMobile]);

  // Prevent body scroll when sidebar open on mobile
  useEffect(() => {
    if (sidebarOpen && isMobile) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'auto';
    }
    return () => { document.body.style.overflow = 'auto'; };
  }, [sidebarOpen, isMobile]);

  // Navigation based on active role
  const getNavigation = () => {
    switch (activeRole) {
      case 'lead_one': // Admin
        return [
          { name: 'Dashboard', path: '/admin/dashboard', icon: 'fa-chart-line' },
          // QUIZ SECTION
          { name: 'Quiz List', path: '/admin/quizzes', icon: 'fa-list' },
          { name: 'Create Quiz', path: '/admin/quizzes/create', icon: 'fa-plus-circle' },
          { name: 'Staff Management', path: '/admin/staff', icon: 'fa-users' },
          { name: 'Student Management', path: '/admin/students', icon: 'fa-graduation-cap' },
          { name: 'Student Enrollment', path: '/admin/student-enrollment', icon: 'fa-user-plus' },
          { name: 'Class Management', path: '/admin/classes', icon: 'fa-school' },
          { name: 'Subject Management', path: '/admin/subjects', icon: 'fa-book' },
          { name: 'Assessment Config', path: '/admin/assessment-config', icon: 'fa-cog' },
          { name: 'Student Fields', path: '/admin/student-fields', icon: 'fa-user' },
          { name: 'Comment Config', path: '/admin/comment-config', icon: 'fa-comment-dots' },
          { name: 'Academic Sessions', path: '/admin/academic-sessions', icon: 'fa-calendar-day' },
          { name: 'Academic Report', path: '/admin/report-data', icon: 'fa-file-invoice' },
          { name: 'Report config', path: '/admin/report-config', icon: 'fa-file-alt' },
          { name: 'Master Result Sheet', path: '/admin/master-result-sheet', icon: 'fa-file-alt' },
          { name: 'Fees', path: '/admin/pay-fee', icon: 'fa-file-alt' },
          { name: 'Wallet', path: '/admin/wallet', icon: 'fa-wallet' },
          { name: 'Student Fees', path: '/admin/fees-management', icon: 'fa-wallet' },
          { name: 'Fees collections', path: '/admin/fee-collection', icon: 'fa-wallet' },
          
          { name: 'Storage Management', path: '/admin/storage-management', icon: 'fa-database' },
          { name: 'Profile Settings', path: '/profile', icon: 'fa-user-cog' },
        ];
      case 'lead_two': // Form Master
        return [
          { name: 'Dashboard', path: '/form-master/dashboard', icon: 'fa-chart-line' },
          { name: 'My Classes', path: '/form-master/class-students', icon: 'fa-school' },
          { name: 'Calculate Grades', path: '/form-master/calculate-grades', icon: 'fa-calculator' },
          { name: 'Master Result Sheet', path: '/form-master/master-result-sheet', icon: 'fa-file-alt' },
          { name: 'Attendance', path: '/form-master/attendance', icon: 'fa-solid fa-calendar-check' },
           { name: 'Profile Settings', path: '/profile', icon: 'fa-user-cog' },
        ];
      case 'lead_four': // Subject Teacher
        return [
          { name: 'Dashboard', path: '/teacher/dashboard', icon: 'fa-chart-line' },
         
           // QUIZ SECTION
          { name: 'Quiz List', path: '/teacher/quizzes', icon: 'fa-list' },
          { name: 'Create Quiz', path: '/teacher/quizzes/create', icon: 'fa-plus-circle' },
          { name: 'Capture Assessment', path: '/teacher/capture-assessment', icon: 'fa-edit' },
          { name: 'Subject Grade', path: '/teacher/subject-grade', icon: 'fa-chart-line' },
          { name: 'Subject Ranking', path: '/teacher/subject-ranking', icon: 'fa-chart-line' },
           { name: 'Profile Settings', path: '/profile', icon: 'fa-user-cog' },
        ];
      case 'lead_five': // Student
        return [
          { name: 'Dashboard', path: '/student/dashboard', icon: 'fa-chart-line' },
           { name: 'My Quizzes', path: '/student/quizzes', icon: 'fa-question-circle' },
          { name: 'My Grades', path: '/student/grades', icon: 'fa-award' },
          { name: 'Print Result', path: '/student/student-result-page', icon: 'fa-result' },
          { name: 'My Profile', path: '/student/profile', icon: 'fa-user' },
        ];
      case 'master_admin': // Master Admin
        return [
          { name: 'Dashboard', path: '/master/dashboard', icon: 'fa-chart-line' },
          { name: 'All Schools', path: '/master/schools', icon: 'fa-school' },
          { name: 'Pending Approvals', path: '/master/pending', icon: 'fa-clock' },
          { name: 'Payments', path: '/master/payments', icon: 'fa-credit-card' },
          { name: 'Transactions', path: '/master/transactions', icon: 'fa-exchange-alt' },
          { name: 'Manual Requests', path: '/master/manual-requests', icon: 'fa-hand-holding-usd' },
        ];
      default:
        // return [];
        return [
          { name: 'Dashboard', path: '/student/dashboard', icon: 'fa-chart-line' },
           { name: 'My Quizzes', path: '/student/quizzes', icon: 'fa-question-circle' },
          { name: 'My Grades', path: '/student/grades', icon: 'fa-award' },
          // { name: 'Print Result', path: '/student/student-result-page', icon: 'fa-result' },
          { name: 'Print Result', path: '/student/student-result-page', icon: 'fa-print' },
          { name: 'My Profile', path: '/student/profile', icon: 'fa-user' },
        ];
    }
  };

  const navigation = getNavigation();

  const getRoleName = (role) => {
    const roles = {
      'lead_one': 'Administrator',
      'lead_two': 'Form Master',
      'lead_three': 'Admission Officer',
      'lead_four': 'Subject Teacher',
      'lead_five': 'Student',
      'master_admin': 'Master Administrator'
    };
    return roles[role] || role;
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Mobile Sidebar Overlay */}
      {sidebarOpen && isMobile && (
        <div 
          className="fixed inset-0 z-40 bg-black bg-opacity-50 transition-opacity duration-300 lg:hidden"
          onClick={() => setSidebarOpen(false)}
          aria-hidden="true"
          style={{ touchAction: 'none' }}
        />
      )}

      {/* Sidebar */}
      <aside className={`
        sidebar-container
        fixed inset-y-0 left-0 z-50 
        w-64 bg-white shadow-xl
        transform transition-transform duration-300 ease-in-out
        lg:transform-none lg:translate-x-0
        flex flex-col
        h-screen
        ${sidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
      `}>
        {/* Sidebar Header */}
        <div className="flex items-center justify-between h-16 px-4 border-b border-gray-200 flex-shrink-0">
          <div className="flex items-center">
            <i className="fas fa-school text-2xl text-indigo-600 mr-3"></i>
            <span className="text-xl font-bold text-gray-800 truncate">SchoolSuite</span>
          </div>
          <button
            onClick={toggleSidebar}
            className="lg:hidden text-gray-500 hover:text-gray-700 p-1"
            aria-label="Close sidebar"
          >
            <i className="fas fa-times text-lg"></i>
          </button>
        </div>

        {/* User Profile Section */}
        <div className="p-4 border-b border-gray-200 flex-shrink-0">
          <div className="flex items-center">
            <div className="w-10 h-10 bg-indigo-100 rounded-full flex items-center justify-center mr-3 flex-shrink-0">
              <i className="fas fa-user text-indigo-600"></i>
            </div>
            <div className="min-w-0 flex-1">
              <p className="font-medium text-gray-800 truncate">
                {activeUser?.email || activeUser?.name || 'User'}
              </p>
              <p className="text-sm text-gray-500 truncate">
                {getRoleName(activeRole)}
              </p>
            </div>
          </div>
        </div>

        {/* Navigation Menu */}
        <nav className="flex-1 overflow-y-auto py-4">
          <ul className="space-y-1 px-3">
            {navigation.map((item) => (
              <li key={item.name}>
                <Link
                  to={item.path}
                  onClick={handleLinkClick}
                  className={`
                    flex items-center px-3 py-3 rounded-lg transition-all duration-200 
                    ${location.pathname.startsWith(item.path)
                      ? 'bg-indigo-50 text-indigo-700 border-l-4 border-indigo-600 pl-2'
                      : 'text-gray-700 hover:bg-gray-100 hover:text-gray-900 border-l-4 border-transparent'
                    }
                    text-sm sm:text-base
                  `}  
                  style={{ textDecoration: "none" }}
                >
                  <i className={`fas ${item.icon} mr-3 w-5 text-center text-gray-500`}></i>
                  <span className="truncate">{item.name}</span>
                </Link>
              </li>
            ))}
            
            {/* Logout Button */}
            <li className="mt-8 px-3">
              <button
                onClick={handleLogout}
                className="flex items-center w-full px-3 py-3 text-gray-700 hover:bg-red-50 hover:text-red-700 rounded-lg transition-all duration-200 border-l-4 border-transparent text-sm sm:text-base"
              >
                <i className="fas fa-sign-out-alt mr-3 w-5 text-center text-gray-500"></i>
                <span>Logout</span>
              </button>
            </li>
          </ul>
        </nav>

        {/* Sidebar Footer */}
        <div className="p-4 border-t border-gray-200 flex-shrink-0">
          <div className="text-xs text-gray-500 text-center">
            © {new Date().getFullYear()} SchoolSuite system
            <div className="mt-1 text-[10px] text-gray-400">
              Powered by <span className="font-medium">cbthost.com</span>
            </div>
          </div>
        </div>
      </aside>

      {/* Main Content */}
      <div className={`
        min-h-screen w-full
        transition-all duration-300
        lg:pl-64
      `}>
        {/* Top Bar */}
        <header className="
          sticky top-0 z-30
          bg-white shadow-md border-b border-gray-200
          h-16 flex items-center
          w-full
        ">
          <div className="px-4 sm:px-6 lg:px-8 w-full">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-4">
                <button
                  onClick={toggleSidebar}
                  className="menu-toggle-button lg:hidden text-gray-600 hover:text-gray-900 p-2 rounded-lg hover:bg-gray-100 active:bg-gray-200 transition-colors"
                  aria-label="Toggle sidebar"
                  type="button"
                >
                  <i className="fas fa-bars text-lg"></i>
                </button>
                
                <h1 className="text-lg font-semibold text-gray-800 truncate max-w-[150px] sm:max-w-none">
                  {navigation.find(nav => location.pathname.startsWith(nav.path))?.name || 'Dashboard'}
                </h1>
              </div>

              <div className="flex items-center space-x-3 sm:space-x-4">
                <button 
                  className="
                    relative p-2 text-gray-500 hover:text-gray-700 
                    rounded-lg hover:bg-gray-100 transition-colors
                    active:bg-gray-200
                  "
                  aria-label="Notifications"
                  type="button"
                >
                  <i className="fas fa-bell text-lg"></i>
                  <span className="
                    absolute -top-0.5 -right-0.5 
                    w-2 h-2 bg-red-500 rounded-full
                    animate-pulse
                  "></span>
                </button>

                <div className="hidden sm:flex items-center space-x-3">
                  <div className="w-8 h-8 bg-indigo-100 rounded-full flex items-center justify-center flex-shrink-0">
                    <i className="fas fa-user text-indigo-600 text-sm"></i>
                  </div>
                  <div className="hidden md:block">
                    <p className="text-sm font-medium text-gray-700 truncate max-w-[150px]">
                      {activeUser?.email || activeUser?.name}
                    </p>
                    <p className="text-xs text-gray-500 truncate max-w-[150px]">
                      {getRoleName(activeRole)}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </header>

        <main className="
          min-h-[calc(100vh-4rem)]
          overflow-y-auto
          bg-gray-50
          p-3 sm:p-4 md:p-6 lg:p-8
        ">
          <div className="max-w-full mx-auto">
            {children}
          </div>
        </main>
      </div>
    </div>
  );
};

export default Layout;

